import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import ErrorBoundary from '../components/common/ErrorBoundary';
import AchievementGrid from '../components/AchievementGrid';
import { toast } from 'sonner';
import { Star, Award, Heart, MapPin, Briefcase, Globe, MessageSquare, Calendar, ChevronLeft, Send, ArrowRight, CheckCircle, Zap, Flame, Trophy } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function SalespersonProfilePage() {
  const { id: userId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shortlisted, setShortlisted] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [myPosts, setMyPosts] = useState([]);
  const [invitingPostId, setInvitingPostId] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/salespeople/${userId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch profile');
      const data = await res.json();
      setProfile(data);
      setShortlisted(data.shortlisted);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const toggleShortlist = async () => {
    try {
      const method = shortlisted ? 'DELETE' : 'POST';
      const res = await fetch(`${API}/business/shortlist/${userId}`, {
        method,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      setShortlisted(!shortlisted);
      toast.success(shortlisted ? 'Removed from shortlist' : 'Added to shortlist');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openInviteModal = async () => {
    try {
      const res = await fetch(`${API}/business/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch posts');
      const data = await res.json();
      setMyPosts(data.filter(p => p.status === 'ACTIVE'));
      setInviteModalOpen(true);
    } catch (error) {
      toast.error('Failed to load your posts');
    }
  };

  const sendInvite = async (postId) => {
    try {
      setInvitingPostId(postId);
      const res = await fetch(`${API}/business/invite/${userId}?post_id=${postId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send invite');
      }
      toast.success(`Invite sent to ${profile?.name}`);
      setInviteModalOpen(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInvitingPostId(null);
    }
  };

  const badgeConfig = {
    GOLD: { label: 'Gold', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30', icon: '1st' },
    SILVER: { label: 'Silver', color: 'text-slate-300 bg-slate-300/10 border-slate-300/30', icon: '2nd' },
    BRONZE: { label: 'Bronze', color: 'text-amber-600 bg-amber-600/10 border-amber-600/30', icon: '3rd' }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="text-brand-muted">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-brand-muted mb-4">Profile not found</p>
          <button onClick={() => navigate(-1)} className="text-brand-gold hover:underline">Go Back</button>
        </div>
      </div>
    );
  }

  const b = profile.reputation?.badge ? badgeConfig[profile.reputation.badge] : null;
  const isBusiness = user?.role === 'BUSINESS';

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-brand-bg" data-testid="salesperson-profile">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back nav */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-brand-muted hover:text-brand-text text-sm mb-6"
            data-testid="profile-back-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          {/* Profile Header */}
          <div className="rounded-xl border border-white/10 bg-[#111317] p-6 sm:p-8 mb-6">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                {profile.avatar ? (
                  <img src={profile.avatar} alt={profile.name} className="w-24 h-24 rounded-full object-cover border-2 border-brand-gold/30" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold font-bold text-3xl border-2 border-brand-gold/30">
                    {profile.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <h1 className="text-2xl font-bold text-brand-text">{profile.name}</h1>
                  {b && (
                    <span className={`text-xs px-2 py-1 rounded border font-semibold ${b.color}`}>
                      <Award className="w-3 h-3 inline mr-1" />{b.label}
                    </span>
                  )}
                  {/* Top Performer Labels */}
                  {profile.labels?.map((label, i) => {
                    const lc = {
                      'Top Rated': { icon: Star, color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' },
                      'Most Deals Won': { icon: Trophy, color: 'text-green-400 bg-green-400/10 border-green-400/30' },
                      'Most Reviewed': { icon: MessageSquare, color: 'text-blue-400 bg-blue-400/10 border-blue-400/30' }
                    }[label];
                    if (!lc) return null;
                    const LIcon = lc.icon;
                    return (
                      <span key={i} data-testid="profile-label" className={`text-xs px-2 py-1 rounded border font-semibold ${lc.color}`}>
                        <LIcon className="w-3 h-3 inline mr-1" />{label}
                      </span>
                    );
                  })}
                </div>

                {profile.regions?.length > 0 && (
                  <div className="flex items-center gap-1 text-sm text-brand-muted mb-2">
                    <MapPin className="w-4 h-4" />{profile.regions.join(', ')}
                  </div>
                )}

                {profile.bio && (
                  <p className="text-brand-text/80 text-sm leading-relaxed mb-4">{profile.bio}</p>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  <StatBox label="Rating" value={profile.reputation?.avgStars?.toFixed(1) || '0.0'} icon={<Star className="w-4 h-4 text-brand-gold fill-brand-gold" />} testid="profile-stat-rating" />
                  <StatBox label="Reviews" value={profile.reputation?.reviewsCount || 0} icon={<MessageSquare className="w-4 h-4 text-blue-400" />} testid="profile-stat-reviews" />
                  <StatBox label="Deals Won" value={profile.completedDeals || 0} icon={<Briefcase className="w-4 h-4 text-green-400" />} testid="profile-stat-deals" />
                  <StatBox label="XP" value={profile.xp || 0} icon={<Zap className="w-4 h-4 text-purple-400" />} testid="profile-stat-xp" />
                  <StatBox label="Streak" value={profile.streak ? `${profile.streak} mo` : '0'} icon={<Flame className="w-4 h-4 text-orange-400" />} testid="profile-stat-streak" />
                  <StatBox label="Active" value={profile.activePosts || 0} icon={<CheckCircle className="w-4 h-4 text-emerald-400" />} testid="profile-stat-active" />
                </div>
              </div>

              {/* Action Buttons (Business only) */}
              {isBusiness && (
                <div className="flex sm:flex-col gap-2 flex-shrink-0">
                  <button
                    onClick={toggleShortlist}
                    data-testid="profile-shortlist-btn"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${shortlisted ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-white/5 border-white/10 text-brand-text hover:border-red-400/40'}`}
                  >
                    <Heart className={`w-4 h-4 ${shortlisted ? 'fill-red-500' : ''}`} />
                    {shortlisted ? 'Saved' : 'Save'}
                  </button>
                  <button
                    onClick={openInviteModal}
                    data-testid="profile-invite-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-gold text-black font-medium hover:bg-brand-gold/90 text-sm"
                  >
                    <Send className="w-4 h-4" />
                    Invite to Sell
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Skills */}
            <DetailCard title="Skills" testid="profile-skills">
              {profile.skills?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill, i) => (
                    <span key={i} className="text-sm px-3 py-1 rounded-lg bg-white/5 text-brand-text border border-white/10">{skill}</span>
                  ))}
                </div>
              ) : (
                <p className="text-brand-muted text-sm">No skills listed</p>
              )}
            </DetailCard>

            {/* Industries */}
            <DetailCard title="Industries" testid="profile-industries">
              {profile.industries?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.industries.map((ind, i) => (
                    <span key={i} className="text-sm px-3 py-1 rounded-lg bg-brand-gold/5 text-brand-gold/80 border border-brand-gold/10">{ind}</span>
                  ))}
                </div>
              ) : (
                <p className="text-brand-muted text-sm">No industries listed</p>
              )}
            </DetailCard>

            {/* Languages */}
            <DetailCard title="Languages" testid="profile-languages">
              {profile.languages?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.languages.map((lang, i) => (
                    <span key={i} className="text-sm px-3 py-1 rounded-lg bg-blue-500/5 text-blue-400/80 border border-blue-500/10">{lang}</span>
                  ))}
                </div>
              ) : (
                <p className="text-brand-muted text-sm">No languages listed</p>
              )}
            </DetailCard>

            {/* Territories */}
            <DetailCard title="Territories" testid="profile-territories">
              {profile.territories?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.territories.map((terr, i) => (
                    <span key={i} className="text-sm px-3 py-1 rounded-lg bg-green-500/5 text-green-400/80 border border-green-500/10">
                      <Globe className="w-3 h-3 inline mr-1" />{terr}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-brand-muted text-sm">No territories listed</p>
              )}
            </DetailCard>
          </div>

          {/* Achievements */}
          {profile.achievements && (
            <div className="mb-6">
              <AchievementGrid achievements={profile.achievements} />
            </div>
          )}

          {/* Member Info */}
          <div className="rounded-xl border border-white/10 bg-[#111317] p-4 mb-6">
            <div className="flex items-center gap-6 text-sm text-brand-muted">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Member since {profile.memberSince ? new Date(profile.memberSince).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'}
              </div>
              {profile.lastActiveAt && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  Last active {new Date(profile.lastActiveAt).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>

          {/* Reviews */}
          <div className="rounded-xl border border-white/10 bg-[#111317] p-6">
            <h2 className="text-lg font-semibold text-brand-text mb-4" data-testid="profile-reviews-heading">
              Reviews ({profile.reputation?.reviewsCount || 0})
            </h2>
            {profile.recentReviews?.length > 0 ? (
              <div className="space-y-4">
                {profile.recentReviews.map((review) => (
                  <div key={review.id} data-testid="profile-review" className="p-4 rounded-lg bg-brand-bg border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-3.5 h-3.5 ${i < review.stars ? 'text-brand-gold fill-brand-gold' : 'text-zinc-700'}`} />
                          ))}
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded bg-white/5 text-brand-muted">{review.fromRole}</span>
                      </div>
                      <span className="text-xs text-brand-muted">{new Date(review.createdAt).toLocaleDateString()}</span>
                    </div>
                    {review.comment && <p className="text-sm text-brand-text/80">{review.comment}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-brand-muted text-sm">No reviews yet</p>
            )}
          </div>
        </div>

        {/* Invite Modal */}
        {inviteModalOpen && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setInviteModalOpen(false)}>
            <div className="bg-[#111317] border border-white/10 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-brand-text mb-2">Invite {profile.name} to Sell</h3>
              <p className="text-sm text-brand-muted mb-4">Select a post to invite this salesperson to:</p>
              {myPosts.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-brand-muted text-sm mb-3">You have no open posts</p>
                  <button onClick={() => { setInviteModalOpen(false); navigate('/business/dashboard'); }} className="text-brand-gold hover:underline text-sm">
                    Create a post first
                  </button>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {myPosts.map((post) => (
                    <button
                      key={post.id}
                      onClick={() => sendInvite(post.id)}
                      disabled={invitingPostId === post.id}
                      data-testid="invite-post-option"
                      className="w-full text-left p-3 rounded-lg bg-brand-bg border border-white/5 hover:border-brand-gold/30 transition-colors flex items-center justify-between disabled:opacity-50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-brand-text text-sm font-medium truncate">{post.title}</div>
                        <div className="text-xs text-brand-muted">{post.status}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-brand-muted flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={() => setInviteModalOpen(false)}
                className="w-full mt-4 px-4 py-2 rounded-lg border border-white/10 text-brand-text hover:bg-white/5 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

const StatBox = ({ label, value, icon, testid }) => (
  <div className="rounded-lg bg-brand-bg border border-white/5 p-3 text-center" data-testid={testid}>
    <div className="flex items-center justify-center gap-1.5 mb-1">
      {icon}
      <span className="text-brand-text font-bold text-lg">{value}</span>
    </div>
    <div className="text-xs text-brand-muted">{label}</div>
  </div>
);

const DetailCard = ({ title, children, testid }) => (
  <div className="rounded-xl border border-white/10 bg-[#111317] p-5" data-testid={testid}>
    <h3 className="text-sm font-semibold text-brand-text mb-3">{title}</h3>
    {children}
  </div>
);

export default SalespersonProfilePage;
